import { useEffect, useState } from "react";

/**
 * useFiltersSnapshot — persistance générique des filtres d'une page
 * dans `sessionStorage`, avec TTL et lecture unique au montage.
 *
 * Extrait du pattern déployé sur `pages/Documents.jsx` (`docs:filters:v1`)
 * et `pages/Avi.jsx` (`avi:filters:v1`) — spec rev177 (2026-02).
 *
 * @param {string} key       Clé sessionStorage (ex : "docs:filters:v1")
 * @param {object} values    Snapshot courant à sérialiser (dictionnaire
 *                           plat sérialisable JSON). L'auto-save se
 *                           re-déclenche à chaque changement d'identité
 *                           d'une des valeurs, donc l'appelant doit
 *                           passer un objet **stable** (composé de
 *                           primitives et de tableaux/Sets convertis).
 * @param {object} [opts]
 * @param {number} [opts.ttlMs=300000]  Durée de vie (ms). 5 min par défaut.
 *
 * @returns {object|null}   `initialSnap` — le dernier snapshot valide
 *                          lu au montage (ou `null` si absent/expiré).
 */
export default function useFiltersSnapshot(key, values, opts = {}) {
  const ttlMs = opts.ttlMs ?? Infinity; // iter130 — état de liste conservé toute la session

  // Lecture unique au montage.
  const [initialSnap] = useState(() => {
    try {
      const raw = sessionStorage.getItem(key);
      if (!raw) return null;
      const snap = JSON.parse(raw);
      if (!snap?.saved_at || Date.now() - snap.saved_at > ttlMs) return null;
      return snap;
    } catch {
      return null;
    }
  });

  // Auto-save à chaque changement d'une valeur pertinente.
  useEffect(() => {
    try {
      sessionStorage.setItem(key, JSON.stringify({ saved_at: Date.now(), ...values }));
    } catch { /* quota — ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, ...Object.values(values)]);

  return initialSnap;
}
